import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IBulletTrainFeature } from 'flagsmith';
import { translate } from '../../../utils/locale';
import { DEFAULT_PAGES_WITHOUT_FILTER_BAR } from '../../../utils/filterBarPages';
import { hasPermission } from 'features/app/redux/permissions';
import JSON5 from 'json5';

const initialState: Features.State = {
	loadingFlags: true,
	flags: {
		alps_summit: true,
		cache_subject_page_data: false,
		can_view_pdf_subject_filter: true,
		can_view_sidebar: true,
		comparisons_high_mid_low: true,
		embargo_feature: true,
		enable_connect_data: true,
		enable_error_boundary: true,
		filters_high_mid_low: true,
		groups_monitoring_timeline: true,
		groups_view_as_school: true,
		hide_qi8: true,
		home_page_export_modal: false,
		ks4_perf_measures: true,
		ks4_perf_measures_spo: true,
		ks4_pms_student_table: false,
		ks4_progress_8: true,
		ks4_progress_8_spo: true,
		ks4_subject_pages: false,
		ks5_a_level_perf_measures: true,
		ks5_ib_perf_measures: true,
		ks5_perf_measures: false,
		maintenance_mode: false,
		my_reports: true,
		pa_display_precision: true,
		reporting_season: false,
		show_auto_renew_toggle: true,
		show_contracts: true,
		show_error_boundary_info: false,
		show_status_toggle: true,
		show_under_target_columns: false,
		strategic_analysis: true,
		subjects_page_save_reports: true,
		subscriptions: true,
		zoho_help: true,
		news_feed_enabled: true,
		strategic_embedded_tables: true,
		spo_meg_count_columns: true,
		enable_paywall_restrictions: true,
		alternative_pa: true,
		client_benchmark: true,
		summit_benchmark: true,
		show_change_prior_attainments: true,
		subject_area_attainment_profile: true,
		trends_pt: false,
		enable_mfa: true,
		enable_delete_user: true,
		enable_meg_roy_export: true,
		student_vas: true,
		alps_annual_census: true,
		hide_organisation_management: true,
		summit_ks5: true,
		summit_ks4_pm: false,
		summit_ks4_pm_group_overview: false,
		summit_ks4_pm_group_setup: false,
		enable_reasons_for_leaving: true,
		new_group_reports: true,
		show_next_academic_year: false,
		enable_mis_integrations: false,
		client_value_mapping: false,
		show_historical_group_reports: true,
		expanded_ks5_performance_measures: false,
		ks5_pm_summary: false,
		ks5_pm_agq: false,
		ks5_pm_tech_level: false,
		ks5_pm_academic: false,
		expanded_summit_ks5_performance_measures: false,
		summit_ks5_pm_group_overview_summary: false,
		summit_ks5_pm_group_overview_a_level: false,
		summit_ks5_pm_group_overview_agq: false,
		summit_ks5_pm_group_overview_tech_level: false,
		summit_ks5_pm_group_overview_academic: false,
		summit_ks5_pm_individual_summary: false,
		summit_ks5_pm_individual_a_level: false,
		summit_ks5_pm_individual_agq: false,
		summit_ks5_pm_individual_tech_level: false,
		summit_ks5_pm_individual_academic: false,
		use_excel_runner: true,
		show_teaching_sets_dropdown: true,
		census_window: true,
		ib_strategic_exports: true,
		capped_9_export: false,
		alps_ascent: true,
	},
	config: {
		maintenance_title: translate('error.maintenance.TITLE') as string,
		maintenance_message: translate('error.maintenance.MESSAGE') as string,
		zoho_link: 'https://alpseducation.zohodesk.eu/portal',
		tag_manager_id: 'GTM-W4396S2',
		error_boundary_blacklist: {
			types: [],
			messages: ['blocked a frame with origin'],
		},
		census_window: {
			start: '2025/11/01 00:00:01',
			end: '2026/03/31 23:59:59',
		},
		pm_pages_without_filter_bar: DEFAULT_PAGES_WITHOUT_FILTER_BAR,
		ib_strategic_exports: {
			pdf: true,
			excel: false,
			benchmarkSetValue: 'Set-2025-Eng-IB-Strat/Set-2025-Eng-IB-Strat.xml',
		},
		bbpa_multi_year_tables: {
			// KS5 (A / AS)
			POINTSPERSUBJECT: true,
			// KS4
			GCSESCORES: true,
			BTECSCORES: true,
			CAMNATSCORES: true,
			EBACCSCORES: true,
			SUBSCORES: true,
			// BTEC
			EXTCERTPOINTS: true,
			DIPPOINTS: true,
			EXTDIPPOINTS: true,
			BTEC2010POINTS: true,
			CACHEAWARDPOINTS: true,
			CACHECERTPOINTS: true,
			CACHEDIPPOINTS: true,
			CACHEEXTDIPPOINTS: true,
			UALDIPPOINTS: true,
			UALEXTDIPPOINTS: true,
		},
		benchmark_urls: {
			england_table_ks5:
				'https://alps.education/wp-content/uploads/2026/02/england_table_ks5_25_26.png',
			england_table_ks4: 'https://alps.education/wp-content/uploads/2026/02/england_table_ks4.png',
			wales_table_ks5: 'https://alps.education/wp-content/uploads/2026/02/wales_table_ks5.png',
			wales_table_ks4: 'https://alps.education/wp-content/uploads/2026/02/wales_table_ks4.png',
			ind_table_ks5:
				'https://alps.education/wp-content/uploads/2026/02/ind_table_ks5_2025_2026.png',
			ind_table_ks4: 'https://alps.education/wp-content/uploads/2026/02/ind_table_ks4.png',
		},
		early_access_ks4: {
			start: '2025/08/20 00:00:01',
			end: '2025/08/21 09:30:00',
		},
		early_access_ks5: {
			start: '2025/08/13 00:00:01',
			end: '2025/08/14 09:30:00',
		},
		password_complexity: {
			LowerCase: '(?=.*?[A-Z])',
			UpperCase: '(?=.*?[a-z])',
			Numbers: '(?=.*?[0-9])',
			SpecialCharacters: '(?=.*?[#?!@$%^&*-])',
			Length: '.{8,}',
		},
		avaliable_benchmarks: ['England', 'Wales', 'Jersey'],
		hide_customer_benchmark: [''],
		terms_and_condition_url:
			'https://alps.education/wp-content/uploads/2024/08/A-Alps-Site-Terms-GDPR_Final-June-2024-1.pdf',
		alps_ascent: 'https://ascent.alps.education/login',
		subscription_pricing: {
			KS4: { type: 'KS4' },
			KS5: { type: 'KS5' },
			KS4KS5: { type: 'KS4KS5' },
		},
		// Fallback end dates used when Flagsmith has not yet loaded. Overridden at runtime by the Flagsmith config.
		academic_year_end_dates_for_rollover: {
			y2024: '2025-07-01',
			y2025: '2026-07-01',
			y2026: '2027-07-01',
			y2027: '2028-07-01',
			y2028: '2029-07-01',
			y2029: '2030-07-01',
			y2030: '2031-07-01',
			y2031: '2032-07-01',
			y2032: '2033-07-01',
			y2033: '2034-07-01',
			y2034: '2035-07-01',
			y2035: '2036-07-01',
		},
		// Fallback values used when Flagsmith has not yet loaded. Overridden at runtime by the Flagsmith config.
		academic_year_end_dates_for_new_group_reports_rollover: {
			y2025: { date: '2025-08-01', label: '2025/26', value: 2024 },
			y2026: { date: '2026-08-01', label: '2026/27', value: 2025 },
			y2027: { date: '2027-08-01', label: '2027/28', value: 2026 },
			y2028: { date: '2028-08-01', label: '2028/29', value: 2027 },
			y2029: { date: '2029-08-01', label: '2029/30', value: 2028 },
			y2030: { date: '2030-08-01', label: '2030/31', value: 2029 },
		},
	},
};

export const features = createSlice({
	name: 'features',
	initialState,
	reducers: {
		setFeatureFlag(
			state: Features.State,
			action: PayloadAction<{ id: keyof Features.Flags; enabled: boolean }>
		): Features.State {
			return {
				...state,
				flags: {
					...state.flags,
					[action.payload.id]: action.payload.enabled,
				},
			};
		},

		setFeatureConfig(
			state: Features.State,
			action: PayloadAction<{ id: keyof Features.Config; value: string }>
		): Features.State {
			let value = action.payload.value;

			try {
				value = JSON5.parse(action.payload.value);
			} catch (e) {}

			return {
				...state,
				config: {
					...state.config,
					[action.payload.id]: value,
				},
			};
		},

		setLoadingFlags(state: Features.State, action: PayloadAction<boolean>) {
			return {
				...state,
				loadingFlags: action.payload,
			};
		},

		setBulkFlagsAndConfig(
			state: Features.State,
			action: PayloadAction<{ flags: { [id: string]: boolean }; config: { [id: string]: string } }>
		): Features.State {
			// parse each config value as JSON if applicable
			const config = Object.entries(action.payload.config).reduce((config, [key, value]) => {
				let parsedValue = value;

				try {
					parsedValue = JSON5.parse(value);
				} catch (e) {}

				return {
					...config,
					[key]: parsedValue,
				};
			}, {});

			return {
				...state,
				loadingFlags: false,
				flags: {
					...state.flags,
					...action.payload.flags,
				},
				config: {
					...state.config,
					...config,
				},
			};
		},
	},
});

export default features.reducer;

export const {
	setFeatureFlag,
	setFeatureConfig,
	setLoadingFlags,
	setBulkFlagsAndConfig,
} = features.actions;

// Get default flags from initial state, formatted for flagsmith
export const getInitialFlags = (): { [feature: string]: IBulletTrainFeature } => {
	if (
		typeof window.flagsmithDefaultFlags === 'object' &&
		Object.keys(window.flagsmithDefaultFlags ?? {}).length
	) {
		return window.flagsmithDefaultFlags;
	}

	return Object.entries(initialState.flags).reduce((struct, [id, enabled]) => {
		return {
			...struct,
			[id]: {
				enabled,
			},
		};
	}, {});
};

export const getInitialConfig = () => {
	return Object.entries(initialState.config).reduce((struct, [id, value]) => {
		return {
			...struct,
			[id]: {
				value: typeof value === 'string' ? value : JSON.stringify(value),
			},
		};
	}, {});
};

export const getFeature = (featureId: keyof Features.Flags) => (state: RootState): boolean => {
	return (
		((state.features as Features.State).flags[featureId] ?? false) &&
		hasPermission(featureId)(state)
	);
};

export const getConfig = (configId: keyof Features.Config) => (state: RootState): any => {
	return (state.features as Features.State).config[configId];
};

export const getLoadingFlags = (state: RootState): boolean => {
	return state.features.loadingFlags;
};
